import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { BizsolUserInfo } from '@src/api/company/auth/bizsol/bizsolApiClient';
import {
  CompanyUserCreateErrorResponse,
  companyUserRegisterApiClient,
} from '@src/api/company/auth/companyUserRegisterApiClient';
import { CLOSING_MONTH } from '@src/constants/companies/closingMonth';
import { PREFECTURES } from '@src/constants/companies/prefectures';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { useScrollToError } from '@src/hooks/useScrollToError';
import { COMPANY_URL } from '@src/routes/company';
import Button from '@src/ui/elements/button/Button';
import { ErrorMessage } from '@src/ui/elements/form';
import Checkbox from '@src/ui/elements/form/Checkbox';
import Input from '@src/ui/elements/form/Input';
import Select from '@src/ui/elements/form/Select';
import Textarea from '@src/ui/elements/form/Textarea';
import { firstError } from '@src/utils/errorMessageUtils';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { EMPLOYEE_SCALES } from '../../../constants/companies/employeeScales';
import { INDUSTRIES } from '../../../constants/companies/industries';
import { REVENUE_SCALES } from '../../../constants/companies/revenueScales';

type formValues = {
  familyName: string;
  firstName: string;
  familyNameKana: string;
  firstNameKana: string;
  establishmentYear: string;
  fiscalMonth: string;
  industry: string;
  serviceDescription: string;
  revenusScale: string;
  employeeScale: string;
  privacyAgreed: boolean;
  email: string;
  birthday: string;
};

const getPrefectureName = (id: string): string => {
  const prefecture = PREFECTURES.find((pref) => pref.id === id);
  return prefecture ? prefecture.name : '';
};

function RegistrationCreateIndex() {
  const location = useLocation();
  const registrationData = location.state?.formValues;
  const [formValues, setFormValues] = useState({
    familyName: '',
    firstName: '',
    familyNameKana: '',
    firstNameKana: '',
    establishmentYear: '',
    fiscalMonth: '',
    industry: '',
    serviceDescription: '',
    revenusScale: '',
    employeeScale: '',
    privacyAgreed: false,
    email: '',
    birthday: '',
  });
  const [errors, setErrors] = useState<CompanyUserCreateErrorResponse>();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const navigate = useNavigate();
  const companyAuth = useCompanyAuth();

  const companyUserCreate = async (formValues: formValues) => {
    if (submitted) {
      return;
    }
    setErrors(undefined);
    setSubmitted(true);

    const params = {
      company: {
        name: registrationData.companyName,
        name_kana: registrationData.companyNameKana,
        postal_code: `${registrationData.postalCodeFirst}${registrationData.postalCodeSecond}`,
        prefecture_code: registrationData.prefectureCode,
        address: registrationData.address,
        family_name: formValues.familyName,
        first_name: formValues.firstName,
        family_name_kana: formValues.familyNameKana,
        first_name_kana: formValues.firstNameKana,
        birthday: formValues.birthday,
        establishment_year: formValues.establishmentYear,
        fiscal_month: formValues.fiscalMonth,
        industry: formValues.industry,
        service_description: formValues.serviceDescription,
        revenus_scale: formValues.revenusScale,
        employee_scale: formValues.employeeScale,
      },
      company_user: {
        family_name: registrationData.familyName,
        first_name: registrationData.firstName,
        family_name_kana: registrationData.familyNameKana,
        first_name_kana: registrationData.firstNameKana,
        email: registrationData.email,
        birthday: registrationData.birthday,
        bizsol_sub: '',
      },
      checkbox: formValues.privacyAgreed,
    };

    if (sessionStorage.getItem('bizsolUserInfo')) {
      // sessionStorage.getItem('bizsolUserInfo')をBizsolUserInfoとして取得
      const bizsolUserInfo: BizsolUserInfo = JSON.parse(
        sessionStorage.getItem('bizsolUserInfo') || '{}',
      );
      if (bizsolUserInfo.sub) {
        params.company_user.bizsol_sub = bizsolUserInfo.sub;
      }
      sessionStorage.removeItem('bizsolUserInfo');
    }

    const response = await companyUserRegisterApiClient.register(params);

    handleApiResponse(
      response,
      (data) => {
        navigate(
          data.requires_user_approval
            ? COMPANY_URL.REGISTRATION_REQUIRES_APPROVAL
            : COMPANY_URL.REGISTRATION_COMPLETE,
        );
      },
      (errors) => {
        setErrors(errors);
        setSubmitted(false);
      },
    );
  };

  const fieldRefs = useScrollToError(errors ?? {});

  return (
    <main className='mx-auto max-w-[800px] py-[60px] lg:px-10 lg:pt-24 sm:px-5'>
      <h2 className=' grid h-[42px] place-items-center text-center text-xl font-semibold'>
        企業アカウントの作成
      </h2>
      <div className='mt-10 flex flex-col'>
        <dl>
          <div className='flex items-center border-b border-gray-200 py-[18px] md:flex-col md:items-start md:gap-1'>
            <dt className='w-[180px] text-sm font-semibold'>企業名</dt>
            <dd className='flex-1 px-8 md:px-0'>{registrationData.companyName}</dd>
          </div>
        </dl>
        <dl>
          <div className='flex items-center border-b border-gray-200 py-[18px] md:flex-col md:items-start md:gap-1'>
            <dt className='w-[180px] text-sm font-semibold'>企業名（フリガナ）</dt>
            <dd className='flex-1 px-8 md:px-0'>{registrationData.companyNameKana}</dd>
          </div>
        </dl>
        <dl>
          <div className='flex items-center border-b border-gray-200 py-[18px] md:flex-col md:items-start md:gap-1'>
            <dt className='w-[180px] text-sm font-semibold'>氏名</dt>
            <dd className='flex-1 px-8 md:px-0'>{`${registrationData.familyName} ${registrationData.firstName}`}</dd>
          </div>
        </dl>
        <dl>
          <div className='flex items-center border-b border-gray-200 py-[18px] md:flex-col md:items-start md:gap-1'>
            <dt className='w-[180px] text-sm font-semibold'>氏名（フリガナ）</dt>
            <dd className='flex-1 px-8 md:px-0'>{`${registrationData.familyNameKana} ${registrationData.firstNameKana}`}</dd>
          </div>
        </dl>
        <dl>
          <div className='flex items-center border-b border-gray-200 py-[18px] md:flex-col md:items-start md:gap-1'>
            <dt className='w-[180px] text-sm font-semibold'>メールアドレス</dt>
            <dd className='flex-1 px-8 md:px-0'>{registrationData.email}</dd>
          </div>
        </dl>
        <dl>
          <div className='flex items-center border-b border-gray-200 py-[18px] md:flex-col md:items-start md:gap-1'>
            <dt className='w-[180px] text-sm font-semibold'>生年月日</dt>
            <dd className='flex-1 px-8 md:px-0'>{registrationData.birthday}</dd>
          </div>
        </dl>
        <dl>
          <div className='flex items-center border-b border-gray-200 py-[18px] md:flex-col md:items-start md:gap-1'>
            <dt className='w-[180px] text-sm font-semibold'>本社所在地</dt>
            <dd className='flex-1 px-8 leading-[1.7em] md:px-0'>
              <span>{`〒${registrationData.postalCodeFirst}-${registrationData.postalCodeSecond}`}</span>
              <br />
              {getPrefectureName(registrationData.prefectureCode)}
              {registrationData.address}
            </dd>
          </div>
        </dl>
      </div>
      <div className='mt-8 flex justify-center gap-5 pt-4'>
        <div className='w-[172px]'>
          <Button
            fullRounded
            fullWidth
            outline
            onClick={() =>
              navigate(COMPANY_URL.REGISTRATION, { state: { formValues: registrationData } })
            }
          >
            前に戻る
          </Button>
        </div>
      </div>
      <h2 className='mt-[50px] grid h-[42px] place-items-center text-center text-xl font-semibold'>
        企業詳細情報の登録
      </h2>
      <form className='mt-10'>
        <div className='flex flex-col gap-[26px]'>
          <div className='flex flex-col gap-[6px]'>
            <div className='flex items-center gap-3'>
              <label className=''>代表者名</label>
              <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                必須
              </div>
            </div>
            <div className='flex h-[50px] gap-4'>
              <div
                className='flex w-full flex-col'
                ref={(el) => (fieldRefs.current['company.family_name'] = el)}
              >
                <Input
                  size='m'
                  type='text'
                  placeholder='山田'
                  value={formValues.familyName}
                  onChange={(e) => setFormValues({ ...formValues, familyName: e.target.value })}
                  error={!!errors?.company.family_name}
                />
                <ErrorMessage>{firstError(errors?.company.family_name)}</ErrorMessage>
              </div>
              <div
                className='flex w-full flex-col'
                ref={(el) => (fieldRefs.current['company.first_name'] = el)}
              >
                <Input
                  size='m'
                  type='text'
                  placeholder='太郎'
                  value={formValues.firstName}
                  onChange={(e) => setFormValues({ ...formValues, firstName: e.target.value })}
                  error={!!errors?.company.first_name}
                />
                <ErrorMessage>{firstError(errors?.company.first_name)}</ErrorMessage>
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-[6px]'>
            <div className='flex items-center gap-3'>
              <label className=''>代表者名（フリガナ）</label>
              <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                必須
              </div>
            </div>
            <div className='flex h-[50px] gap-4'>
              <div
                className='flex w-full flex-col'
                ref={(el) => (fieldRefs.current['company.family_name_kana'] = el)}
              >
                <Input
                  size='m'
                  type='text'
                  placeholder='ヤマダ'
                  value={formValues.familyNameKana}
                  onChange={(e) => setFormValues({ ...formValues, familyNameKana: e.target.value })}
                  error={!!errors?.company.family_name_kana}
                />
                <ErrorMessage>{firstError(errors?.company.family_name_kana)}</ErrorMessage>
              </div>
              <div
                className='flex w-full flex-col'
                ref={(el) => (fieldRefs.current['company.first_name_kana'] = el)}
              >
                <Input
                  size='m'
                  type='text'
                  placeholder='タロウ'
                  value={formValues.firstNameKana}
                  onChange={(e) => setFormValues({ ...formValues, firstNameKana: e.target.value })}
                  error={!!errors?.company.first_name_kana}
                />
                <ErrorMessage>{firstError(errors?.company.first_name_kana)}</ErrorMessage>
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-[6px]'>
            <div className='flex items-center gap-3'>
              <label className=''>代表者生年月日</label>
            </div>
            <div className='flex h-[50px] gap-4'>
              <div
                className='flex w-[200px] flex-col'
                ref={(el) => (fieldRefs.current['company.birthday'] = el)}
              >
                <Input
                  size='m'
                  type='text'
                  placeholder='19900101'
                  value={formValues.birthday}
                  onChange={(e) => setFormValues({ ...formValues, birthday: e.target.value })}
                  error={!!errors?.company.birthday}
                  inputMode='numeric'
                />
                <ErrorMessage>{firstError(errors?.company.birthday)}</ErrorMessage>
              </div>
            </div>
          </div>

          <div className='flex gap-4'>
            <div className='flex w-1/2 flex-col gap-[6px]'>
              <label className=''>設立年</label>
              <div className='flex h-[50px] items-center gap-[8px]'>
                <div
                  className='w-[200px]'
                  ref={(el) => (fieldRefs.current['company.establishment_year'] = el)}
                >
                  <Input
                    size='m'
                    type='text'
                    placeholder='2000'
                    value={formValues.establishmentYear}
                    onChange={(e) =>
                      setFormValues({ ...formValues, establishmentYear: e.target.value })
                    }
                    error={!!errors?.company.establishment_year}
                  />
                </div>
                <div className='flex-1'>年</div>
              </div>
              <ErrorMessage>{firstError(errors?.company.establishment_year)}</ErrorMessage>
            </div>
          </div>

          <div className='flex flex-col gap-[6px]'>
            <div className='flex items-center gap-3'>
              <label className=''>業種</label>
              <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                必須
              </div>
            </div>
            <div
              ref={(el) => (fieldRefs.current['company.industry'] = el)}
              className='flex w-full flex-col'
            >
              <Select
                items={INDUSTRIES}
                size='m'
                data-testid='industry-select'
                initialValue={formValues.industry}
                onSelect={(value) => setFormValues({ ...formValues, industry: value })}
                error={!!errors?.company.industry}
              />
              <ErrorMessage>{firstError(errors?.company.industry)}</ErrorMessage>
            </div>
          </div>

          <div className='flex flex-col gap-[6px]'>
            <label className=''>提供商品・サービス</label>
            <div
              className='flex h-[120px]'
              ref={(el) => (fieldRefs.current['company.service_description'] = el)}
            >
              <Textarea
                placeholder='例：AIを活用したデータ分析サービスや、企業向けクラウドサービスの提供'
                value={formValues.serviceDescription}
                onChange={(e) =>
                  setFormValues({ ...formValues, serviceDescription: e.target.value })
                }
                error={!!errors?.company.service_description}
              />
            </div>
            <ErrorMessage>{firstError(errors?.company.service_description)}</ErrorMessage>
          </div>

          <div className='flex flex-col gap-[6px]'>
            <div className='flex items-center gap-3'>
              <label className=''>売上規模</label>
              <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                必須
              </div>
            </div>
            <div
              ref={(el) => (fieldRefs.current['company.revenus_scale'] = el)}
              className='flex w-full flex-col'
            >
              <Select
                items={REVENUE_SCALES}
                size='m'
                data-testid='revenue-select'
                initialValue={formValues.revenusScale}
                onSelect={(value) => setFormValues({ ...formValues, revenusScale: value })}
                error={!!errors?.company.revenus_scale}
              />
              <ErrorMessage>{firstError(errors?.company.revenus_scale)}</ErrorMessage>
            </div>
          </div>

          <div className='flex flex-col gap-[6px]'>
            <div className='flex items-center gap-3'>
              <label className=''>従業員数規模</label>
              <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                必須
              </div>
            </div>
            <div
              ref={(el) => (fieldRefs.current['company.employee_scale'] = el)}
              className='flex w-full flex-col'
            >
              <Select
                items={EMPLOYEE_SCALES}
                size='m'
                data-testid='employee-select'
                initialValue={formValues.employeeScale}
                onSelect={(value) => setFormValues({ ...formValues, employeeScale: value })}
                error={!!errors?.company.employee_scale}
              />
              <ErrorMessage>{firstError(errors?.company.employee_scale)}</ErrorMessage>
            </div>
          </div>
          <div className='flex flex-col gap-[6px]'>
            <label className=''>決算月</label>
            <div
              ref={(el) => (fieldRefs.current['company.fiscal_month'] = el)}
              className='flex w-full flex-col'
            >
              <Select
                items={CLOSING_MONTH}
                size='m'
                data-testid='fiscal-month-select'
                initialValue={formValues.fiscalMonth}
                onSelect={(value) => setFormValues({ ...formValues, fiscalMonth: value })}
                error={!!errors?.company.fiscal_month}
              />
              <ErrorMessage>{firstError(errors?.company.fiscal_month)}</ErrorMessage>
            </div>
          </div>
        </div>
        <div className='mt-[56px] text-center'>
          <a
            className='cursor-pointer text-primary-500 underline'
            onClick={() => window.open(COMPANY_URL.TERMS, '_blank', 'noopener,noreferrer')}
          >
            利用規約
          </a>
          と
          <a
            className='cursor-pointer text-primary-500 underline'
            onClick={() => window.open(COMPANY_URL.PRIVACY_POLICY, '_blank', 'noopener,noreferrer')}
          >
            プライバシーポリシー
          </a>
          を必ずご確認ください。
        </div>
        <div className='mt-[22px] flex flex-col items-center justify-center gap-3'>
          <div
            className='flex items-center gap-3'
            ref={(el) => (fieldRefs.current['checkbox'] = el)}
          >
            <Checkbox
              id='privacy-check'
              checked={formValues.privacyAgreed}
              onChange={(e) => setFormValues({ ...formValues, privacyAgreed: e.target.checked })}
            />
            <label
              htmlFor='privacy-check'
              className='cursor-pointer text-sm font-semibold'
            >
              上記に同意する
            </label>
          </div>
          <ErrorMessage>{firstError(errors?.checkbox)}</ErrorMessage>
        </div>
        {formValues.privacyAgreed ? (
          <button
            className='btn-login mx-auto mt-6 max-w-[360px] bg-primary-500 text-white'
            type='button'
            onClick={() => companyUserCreate(formValues)}
          >
            {companyAuth.profile?.tenant?.requiredUserApproval ? '登録手続きを進める' : '作成する'}
          </button>
        ) : (
          <button
            className='btn-login pointer-events-none mx-auto mt-6 max-w-[360px] bg-gray-200 text-white'
            type='button'
            disabled={!formValues.privacyAgreed}
            onClick={() => companyUserCreate(formValues)}
          >
            {companyAuth.profile?.tenant?.requiredUserApproval ? '登録手続きを進める' : '作成する'}
          </button>
        )}
      </form>
    </main>
  );
}

export default RegistrationCreateIndex;
